import { Milestone } from '@optools/services/operator-gateway/milestones/types';
import { MilestoneType } from '@optools/services/operator-gateway/types/generated/graphql';
import { formatSecondsHHMMSS } from '@optools/utils/format';
import { isEqual } from 'date-fns';

export function formatInningDetails(
  milestone: Milestone | undefined,
): `${'Top' | 'Bot'} ${string}` | '' {
  if (!milestone) {
    return '';
  }

  const inning = milestone.keywords.find((item) => item.name === 'inning')?.value as string;
  const isTopInning = milestone.keywords.find((item) => item.name === 'isTopInning')
    ?.value as boolean;

  if (!inning) {
    return '';
  }

  return `${isTopInning ? 'Top' : 'Bot'} ${inning}`;
}

/**
 * Determines if a Stream Start warning tooltip should be displayed based in the Milestone Table
 *
 * @param milestoneType - The type of milestone to check.
 * @param programDateTime - The program's date and time, which can be undefined.
 * @param absoluteTime - The absolute timestamp of the milestone to compare against the program date and time.
 * @returns A boolean indicating whether a stream start warning tooltip should be displayed.
 */
export function shouldDisplayStreamStartWarning(
  milestoneType: MilestoneType,
  programDateTime: string | undefined,
  absoluteTime: string,
) {
  return (
    milestoneType === MilestoneType.STREAM_START &&
    programDateTime &&
    !isEqual(absoluteTime, programDateTime)
  );
}

/**
 * Determines whether a Stream Stop warning tooltip should be displayed in the Milestone Table,
 * program date time, duration, and video time.
 *
 * @param milestoneType - The type of milestone to check.
 * @param programDateTime - The program's date and time, which can be undefined.
 * @param duration - The duration of the video.
 * @param videoTime - The current time of the video.
 * @returns A boolean indicating whether a stream stop warning tooltip should be displayed.
 */
export function shouldDisplayStreamStopWarning(
  milestoneType: MilestoneType,
  programDateTime: string | undefined,
  duration: number | null,
  videoTime: number,
) {
  return (
    milestoneType === MilestoneType.STREAM_STOP &&
    programDateTime &&
    typeof duration === 'number' &&
    videoTime !== duration
  );
}

/**
 * Formats a video timestamp into a human-readable string in HH:MM:SS format.
 *
 * @param videoTime - The time in seconds to format. If the time is negative, the formatted string will include a leading '-'.
 * @returns A string representing the formatted time in HH:MM:SS format.
 *
 * @example
 * ```typescript
 * const formattedTimestamp = formatVideoTimestamp(3661) // Output: "01:01:01";
 * ```
 */
export function formatVideoTimestamp(videoTime: number) {
  const timestamp = Math.abs(videoTime ?? 0);
  return `${videoTime && videoTime < 0 ? '-' : ''}${formatSecondsHHMMSS(timestamp)}`;
}

/**
 * Formats the asset URL to include the `_original` suffix before the file extension to access the full video for "Trim Mode".
 *
 * @example
 * // Input URL
 * const url = "https://example.com/video/stream.m3u8";
 *
 * // Output URL after formatting
 * // "https://example.com/video/stream_original.m3u8"
 * const formattedUrl = formatOriginalAssetUrl(url);
 *
 * @param url - The URL string to be formatted
 * @returns The formatted URL with "_original" inserted before the .m3u8 extension
 */
export function formatOriginalAssetUrl(url: string): string {
  return url.replace(/(.*)(\.m3u8)$/, '$1_original$2');
}
